import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Card,
  CardActions,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowRight as ArrowRightIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Notes as NotesIcon
} from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import HoveredCard from '../../../common/card/HoveredCard';
import { formatDate } from '../../../common/formatter';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { useWorkflowGroupPermissions } from '../../workflow/Workflow.hooks';
import TaskAssignDialog, { SubTaskUserItem } from '../dialogs/TaskAssignDialog';
import {
  compareTaskStatus,
  isSubTaskInActiveStatePath,
  parseTaskStatus
} from '../task.helper';
import { useCreateSubTasks, useReassignSubTasks } from '../Task.hooks';
import { BaseTaskCardProps, Task } from '../Task.interface';

const StyledTableRow = styled(TableRow)`
  &.locked {
    opacity: 0.6;
    background-color: ${props => props.theme.palette.grey[100]};
  }
  &.selected {
    background-color: ${props => props.theme.palette.action.hover};
  }
  &:not(.selected) .MuiTableCell-root {
    border-bottom: none;
  }
  .actions {
    padding: 2px;
  }
  .cell-secondary-text {
    white-space: normal;
  }
`;

export interface SubTaskListCardProps extends BaseTaskCardProps {
  task: Task;
  title?: string;
  dialogTitle?: string;
  disabled?: boolean;
}

const SubTaskListCard: React.FC<SubTaskListCardProps> = (
  props: SubTaskListCardProps
) => {
  const { task, title = 'SubTasks', dialogTitle, onChange, onError } = props;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const childrenRef = useRef(task.children || []);
  childrenRef.current = task.children || [];
  const [selected, setSelected] = useState<number | undefined>();
  const [assignProps, setAssignProps] = useState<{
    taskId: number;
    reassign: boolean;
  }>();
  const { groupIds, canReassign } = useReassignSubTasks(task);
  const { hasGroupPermissions } = useWorkflowGroupPermissions(task);
  const { data, isLoading, error, createSubTasks } = useCreateSubTasks(
    task.id || 0
  );
  const disabled = props.disabled || isLoading;
  // make sure to use legacy checks if subTasks of the main task have no groups
  // this is used when reassigning old existing subtasks
  // before the subTask.group implementation
  const isLegacyAssignment =
    task.children &&
    task.children.length > 0 &&
    task.children.every(subTask => !subTask.group);
  // active subTasks are subTasks with a statePath
  // that matches the current main task.status
  const hasActiveSubTasks = (task.children || []).some(subTask => {
    return (
      !subTask.statePath || compareTaskStatus(subTask.statePath, task.status)
    );
  });

  const { lockedUsers, preselectedUsers } = useMemo(() => {
    const subTasks = task.children || [];
    // get all subTasks except for reassigning subTask
    const lockedUsers: SubTaskUserItem[] = [];
    // preselect non-locked selected users
    // for ease of updating the assigned group or optional flag
    const preselectedUsers: SubTaskUserItem[] = [];
    for (const subTask of subTasks) {
      // make sure subTask belongs to current task.status
      if (!subTask.user || !isSubTaskInActiveStatePath(subTask, task)) {
        continue;
      }
      const item: SubTaskUserItem = {
        id: subTask.id,
        version: subTask.version,
        user: subTask.user,
        group: subTask.group,
        statePath: subTask.statePath,
        isOptional: subTask.isOptional
      };
      // all items are locked except for the item to be reassigned
      const isLocked =
        !assignProps?.reassign || assignProps.taskId !== subTask.id;
      const array = isLocked ? lockedUsers : preselectedUsers;
      array.push(item);
    }
    return { lockedUsers, preselectedUsers };
  }, [assignProps, task]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const children = Array.from(childrenRef.current);
    for (const subTask of data.created.concat(data.updated)) {
      const index = children.findIndex(child => child.id === subTask.id);
      if (index > -1) {
        children[index] = { ...children[index], ...subTask };
      } else {
        // add as first entry
        children.unshift(subTask);
      }
    }
    onChangeRef.current?.({ children });
  }, [data]);

  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error]);

  return (
    <Card>
      {isLoading && <LinearProgress />}
      <CardTitle>
        <AssignmentTurnedInIcon />
        {title}
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={1} />
              <TableCell>Assigned</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date and Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {task && task.children ? (
              task.children.map(subTask => {
                const enabled = !disabled && canReassign(subTask);
                const isLocked = !isSubTaskInActiveStatePath(subTask, task);
                const isSelected = selected === subTask.id;
                const classes: string[] = [];
                // only include locked styling if there are also active subTasks
                isLocked && hasActiveSubTasks && classes.push('locked');
                isSelected && classes.push('selected');
                return (
                  <Fragment key={subTask.id}>
                    <StyledTableRow
                      hover={enabled}
                      className={classes.join(' ')}
                      onClick={() => {
                        if (enabled) {
                          setAssignProps({
                            taskId: subTask.id!,
                            reassign: true
                          });
                        }
                      }}
                    >
                      <TableCell className='actions' padding='none'>
                        <IconButton
                          size='small'
                          disabled={!subTask.reason}
                          onClick={event => {
                            event.stopPropagation();
                            setSelected(
                              selected !== subTask.id ? subTask.id : undefined
                            );
                          }}
                        >
                          {isSelected ? (
                            <ArrowDropDownIcon />
                          ) : (
                            <ArrowRightIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {subTask.user?.firstName} {subTask.user?.lastName}
                        {subTask.group && (
                          <Typography
                            variant='body2'
                            color='textSecondary'
                            className='cell-secondary-text'
                          >
                            {subTask.group.name}
                          </Typography>
                        )}
                        {subTask.isOptional && (
                          <Typography
                            display='block'
                            variant='caption'
                            color='textSecondary'
                          >
                            (optional)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TaskStatusLabel task={subTask} />
                        {isLocked && subTask.statePath && (
                          <Typography
                            variant='body2'
                            color='textSecondary'
                            className='cell-secondary-text'
                          >
                            from v{subTask.version}{' '}
                            <strong>
                              {parseTaskStatus(subTask.statePath)}
                            </strong>
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(subTask.modified)}</TableCell>
                    </StyledTableRow>
                    <TableRow>
                      <TableCell colSpan={4} padding='none'>
                        {subTask.reason && (
                          <Collapse in={isSelected}>
                            <HoveredCard
                              icon={<NotesIcon />}
                              title='Reason for Rejection'
                              content={
                                <Typography variant='body1'>
                                  {subTask.reason}
                                </Typography>
                              }
                            />
                          </Collapse>
                        )}
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4}>No subtasks assigned</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CardActions style={{ padding: 16 }}>
        <Button
          color='primary'
          variant='outlined'
          disabled={disabled || !hasGroupPermissions}
          onClick={() => setAssignProps({ taskId: task.id!, reassign: false })}
        >
          Add Reviewers
        </Button>
      </CardActions>
      {assignProps && (
        <TaskAssignDialog
          open
          title={dialogTitle}
          taskId={assignProps.taskId}
          multiple={!assignProps.reassign}
          // always apply groupIds for new behavior
          groupIds={
            !isLegacyAssignment || assignProps.reassign ? groupIds : undefined
          }
          parentTask={task}
          lockedUsers={lockedUsers}
          initialUsers={preselectedUsers}
          isLegacyAssignment={isLegacyAssignment}
          onCancel={() => setAssignProps(undefined)}
          onAssign={(subTask: Task) => {
            const children = Array.from(task.children || []);
            const index: number = children.findIndex(
              (subTask: Task) => subTask.id === assignProps.taskId
            );
            if (index > -1) {
              console.log('replace child task', subTask);
              children.splice(index, 1, subTask);
              // trigger update for TaskStatusActions
              onChange?.({ children });
            }
            setAssignProps(undefined);
          }}
          handleAssign={subTaskUsers => {
            setAssignProps(undefined);
            if (subTaskUsers.length > 0) {
              createSubTasks({ subTaskUsers });
            }
          }}
        />
      )}
    </Card>
  );
};

export default SubTaskListCard;
